import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Grn } from '../../types/grn';
import { finalizeGrn } from '../../services/grnApi';
import { formatCurrency } from '../../utils/currencyUtils';

interface GrnFinalizeModalProps {
  grn: Grn;
  onFinalize: (grn: Grn) => void;
  onCancel: () => void;
}

export default function GrnFinalizeModal({ grn, onFinalize, onCancel }: GrnFinalizeModalProps) {
  const [grnNumber, setGrnNumber] = useState('');
  const [isFinalizing, setIsFinalizing] = useState(false);

  const generateGrnNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    return `GRN${year}${month}${day}${random}`;
  };

  const handleGenerateNumber = () => {
    setGrnNumber(generateGrnNumber());
  };

  const handleFinalize = async () => {
    if (!grnNumber.trim()) {
      alert('Please enter a GRN number');
      return;
    }

    if (!grn.id) {
      alert('GRN ID not found');
      return;
    }

    setIsFinalizing(true);
    try {
      const finalizedGrn = await finalizeGrn(grn.id);
      onFinalize(finalizedGrn);
    } catch (error) {
      console.error('Error finalizing GRN:', error);
      alert('Failed to finalize GRN. Please try again.');
    } finally {
      setIsFinalizing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Finalize GRN
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Once finalized, this GRN cannot be edited. Please review all details before proceeding.
            </p>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">GRN Summary</h4>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <div>Items: {grn.items?.length || 0}</div>
                <div>Received: {grn.received_date} at {grn.received_time}</div>
                <div>Net Total: {formatCurrency(grn.totals_net)}</div>
                <div>VAT: {formatCurrency(grn.totals_vat)}</div>
                <div>Gross Total: {formatCurrency(grn.totals_gross)}</div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                GRN Number
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={grnNumber}
                  onChange={(e) => setGrnNumber(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter GRN number"
                  required
                />
                <button
                  type="button"
                  onClick={handleGenerateNumber}
                  className="px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200"
                >
                  Generate
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleFinalize}
              disabled={isFinalizing || !grnNumber.trim()}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isFinalizing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Finalizing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Finalize GRN
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
